import { getGiftsRulesPublic } from '@agos/api-client/menu'
import type { GiftRule as ServerAmountGiftRule } from '@agos/api-client/model'
import { parseMoneyToCents } from '@agos/shared'

import { lineKey, type CartLine } from './cart'

/**
 * *Buy two pizzas, have a third on us* — the rules behind the gift offer.
 *
 * **Measured 2026-09-27, and the earlier measurement in this comment had expired.** It read *"one rule is
 * active on this data - `categoryKeyword: "pizza"`, `minCount: 2`, `giftName: "Šunková Pizza Darček"`"*.
 * That rule has been `active = f` since 2026-08-10. Šurany's live rules are **`pizza` / `minCount` 1 /
 * *Šunková Pizza* / 3,00 EUR / multiply** and **`študentské menu` / 1 / *Nápoj grátis* / 0,00**; Nesvady's
 * is **`jesenné menu` / 1 / *Kurací vývar 330ml* / 1,50**, created 2026-09-25. All three keywords are
 * catalogue category names, so none is a rule that can never fire.
 *
 * **`minCount: 1` is the part worth knowing**: every single pizza is owed a gift, not every second one.
 * FB-804 found the till counting no assembled pizza at all against that rule, and read this comment as
 * the live figure on the way. A measurement in a comment is a claim with a date on it.
 *
 * The original point stands: a customer with a qualifying basket is owed a gift and this port had no way
 * to offer it - `giftRuleId` was in `CartLine` and in the order payload with no UI anywhere, which is
 * FB-C24's shape again.
 *
 * **The server decides whether it was really earned.** `GiftRules.stripUnearnedGifts` drops the line
 * when the qualifying count is not met, so everything here is a claim the basket makes and the server
 * checks — which is the right way round, and why none of this arithmetic is a security boundary.
 *
 * Three counting rules are legacy's and each one matters:
 *
 * - **A gift never earns a gift.** A line already carrying `giftRuleId` is excluded from the count, or
 *   two pizzas would earn a third, which would earn a fourth.
 * - **A daily-menu dish never counts.** It is priced by its own rules and belongs to a day.
 * - **`multiply` decides whether four pizzas earn one gift or two.** The measured rule multiplies.
 */

export interface GiftRule {
  id: number
  categoryKeyword: string
  minCount: number
  giftName: string
  giftPriceCents: number
  multiply: boolean
  active: boolean
}

export function giftRulesOf(payload: unknown): GiftRule[] {
  if (!Array.isArray(payload)) return []
  const rules: GiftRule[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    const minCount = row.minCount
    const keyword = typeof row.categoryKeyword === 'string' ? row.categoryKeyword.trim().toLowerCase() : ''
    const name = typeof row.giftName === 'string' ? row.giftName.trim() : ''
    // A rule with no keyword matches nothing and a rule with no name cannot be offered; both would be
    // an invisible entry in a list the operator thinks is working.
    if (typeof row.id !== 'number' || keyword === '' || name === '') continue
    if (typeof minCount !== 'number' || !Number.isInteger(minCount) || minCount < 1) continue
    rules.push({
      id: row.id,
      categoryKeyword: keyword,
      minCount,
      giftName: name,
      giftPriceCents:
        typeof row.giftPrice === 'number' || typeof row.giftPrice === 'string'
          ? parseMoneyToCents(row.giftPrice)
          : 0,
      multiply: row.multiply === true,
      active: row.active === true,
    })
  }
  return rules
}

export interface GiftOffer {
  ruleId: number
  /**
   * Which table the rule is in - `FRONTEND.md` §9.697. Absent is a product-count rule, which every offer was before
   * an amount could earn one.
   */
  ruleType?: 'amount'
  giftName: string
  giftPriceCents: number
  /** How many are owed and not yet in the basket. */
  count: number
}

/** A basket line's category name, for matching against a rule's keyword. */
export interface CountableLine {
  line: CartLine
  categoryName: string
}

/**
 * What the basket has earned and not yet taken.
 *
 * `count` is what is still owed — entitled minus what is already in the basket — so an offer that has
 * been accepted stops being made, and a customer who adds two more pizzas is offered another.
 */
export function giftOffers(rules: readonly GiftRule[], lines: readonly CountableLine[]): GiftOffer[] {
  const offers: GiftOffer[] = []
  for (const rule of rules) {
    if (!rule.active) continue

    // §9.697 - an amount gift on the same rule id is not this rule's gift: the two tables share ids by accident.
    const held = heldBy(rule.id, lines)
    const owed = Math.max(0, earnedBy(rule, lines) - held)
    if (owed > 0) {
      offers.push({ ruleId: rule.id, giftName: rule.giftName, giftPriceCents: rule.giftPriceCents, count: owed })
    }
  }
  return offers
}

/**
 * How many of a product rule's gift the basket earns - FRONTEND.md §9.765. One function for the offer and for the
 * removal below, so the basket can never be offered a gift it would then take away, or keep one it would not offer.
 */
export function earnedBy(rule: GiftRule, lines: readonly CountableLine[]): number {
  const qualifying = lines.reduce((total, entry) => {
      // A gift never earns a gift, and a daily-menu dish never counts towards one.
      if (entry.line.giftRuleId !== undefined) return total
      if (entry.line.productId < 0 && entry.line.composed === undefined) return total
      // An assembled line — a kombo — counts: it is pizzas, however it was put together.
      const matches =
        entry.line.composed !== undefined
          ? entry.line.composed.name.toLowerCase().includes(rule.categoryKeyword)
          : entry.categoryName.toLowerCase().includes(rule.categoryKeyword)
      return matches ? total + entry.line.quantity : total
    }, 0)
  return rule.multiply ? Math.floor(qualifying / rule.minCount) : qualifying >= rule.minCount ? 1 : 0
}

/** The quantity of a product rule's gift the basket holds - §9.697: an amount gift with the same id is another rule's. */
function heldBy(ruleId: number, lines: readonly CountableLine[]): number {
  return lines
    .filter((entry) => entry.line.giftRuleId === ruleId && entry.line.giftRuleType !== 'amount')
    .reduce((total, entry) => total + entry.line.quantity, 0)
}

/** One gift line to shorten: its basket key and how many to take off it. */
export interface GiftTakeBack {
  key: string
  quantity: number
}

/**
 * The product gifts the basket holds and no longer earns - owner O126, FRONTEND.md §9.765: *"when I remove something
 * from košík that added Darček for me, darček stays there"*. Per rule, everything held beyond {@link earnedBy}, and
 * all of it when the rule is gone or switched off; an amount gift is {@link amountGiftsToRemove}'s.
 *
 * **`null` rules means the read has not answered, and then nothing is taken** - and the caller passes `null` too while
 * the products or categories have not answered, because a line with no category earns nothing and the gift would go
 * on a blip (FB-831's shape).
 */
export function productGiftsToRemove(
  rules: readonly GiftRule[] | null,
  lines: readonly CountableLine[],
): GiftTakeBack[] {
  if (rules === null) return []
  const takeBack: GiftTakeBack[] = []
  const ruleIds = new Set(
    lines
      .filter((entry) => entry.line.giftRuleId !== undefined && entry.line.giftRuleType !== 'amount')
      .map((entry) => entry.line.giftRuleId as number),
  )
  for (const ruleId of ruleIds) {
    const rule = rules.find((candidate) => candidate.id === ruleId)
    const earned = rule === undefined || !rule.active ? 0 : earnedBy(rule, lines)
    let excess = heldBy(ruleId, lines) - earned
    for (const entry of lines) {
      if (excess <= 0) break
      if (entry.line.giftRuleId !== ruleId || entry.line.giftRuleType === 'amount') continue
      const quantity = Math.min(entry.line.quantity, excess)
      takeBack.push({ key: lineKey(entry.line), quantity })
      excess -= quantity
    }
  }
  return takeBack
}

/**
 * The basket line a gift becomes.
 *
 * `productId: 0` and the rule id beside it — the server matches on `giftRuleId`, and `lineKey` already
 * keeps a gift apart from a bought one of the same thing. Merging them would either give away the paid
 * item or charge for the gift, depending which line won.
 *
 * **The price is the rule's, not zero — §10A.109, the owner's Q2.** *"darček šunková pizza za 3 eurá
 * k objednávke, but when I added it in my košík, I see it as 0,00 euro."* This carried
 * `unitPriceCents: 0`, so the basket showed a free pizza; legacy puts `offer.giftPrice` on the line
 * (`Home.tsx:612`) and sums it like any other. A `product_gift_rules` row is a named item at a
 * **reduced** price - the column is called `gift_price` - and the owner's own rule is 2,00 €.
 *
 * The figure here is what the basket *shows*; the server re-reads it from the rule and is what the
 * customer is *charged*. They have to agree, which is why this changed with §10A.109 rather than
 * after it.
 */
export function giftLine(offer: GiftOffer): CartLine {
  if (offer.ruleType === 'amount') {
    // §9.697 - one, free, and typed, so the server reads the claim in `gift_rules` (§10A.354).
    return {
      productId: 0,
      extras: '',
      quantity: 1,
      giftRuleId: offer.ruleId,
      giftRuleType: 'amount',
      composed: { name: offer.giftName, unitPriceCents: 0 },
    }
  }
  return {
    productId: 0,
    extras: '',
    quantity: offer.count,
    giftRuleId: offer.ruleId,
    composed: { name: offer.giftName, unitPriceCents: offer.giftPriceCents },
  }
}

/**
 * The key an offer is dismissed by - a product rule and an amount rule may carry the same id.
 *
 * `HomePage` remembered dismissals as bare rule ids, so saying *Teraz nie* to the pizza for two would have silenced
 * the Kofola over 70 € on the same id as well.
 */
export function giftOfferKey(offer: Pick<GiftOffer, 'ruleId' | 'ruleType'>): string {
  return `${offer.ruleType ?? 'product'}:${offer.ruleId}`
}

/**
 * *Darčeky podľa sumy* - a gift once the order reaches an amount - `FRONTEND.md` §9.697, owner #21 / O75.
 *
 * **The owner**: *"darčeky by sa mohli dať robiť napríklad nad 70€ kofola zdarma ze máš určitú sumu"*. Decided
 * (#124): **offered**, like the product-count gifts, once **the food total after discounts, without delivery**,
 * reaches the rule's amount; accepted by the customer; **taken back when the basket drops below**. Each reached rule
 * is offered - a franchise writing rules at 50 € and 70 € means both.
 *
 * It had never worked anywhere, legacy included: the rules were saved and nothing read them. The server checks every
 * claim again (§10A.354), so this is the offer and the claim, not the entitlement.
 */
export interface AmountGiftRule {
  id: number
  giftItem: string
  minAmountCents: number
  active: boolean
}

/** The path and the cache key of the public read - §10A.353. One spelling, so the two pages share one request. */
export const AMOUNT_GIFT_RULES_PATH = '/api/v1/gifts/rules/public'
export const AMOUNT_GIFT_RULES_KEY = [AMOUNT_GIFT_RULES_PATH] as const

/**
 * The active amount rules, without a login - `BACKEND.md` §10A.353. Through the generated fetcher since the client
 * was regenerated after the merge; `AMOUNT_GIFT_RULES_KEY` is the same string as its generated query key.
 */
export function fetchAmountGiftRules(): Promise<ServerAmountGiftRule[]> {
  return getGiftsRulesPublic()
}

export function amountGiftRulesOf(payload: unknown): AmountGiftRule[] {
  if (!Array.isArray(payload)) return []
  const rules: AmountGiftRule[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    const item = typeof row.giftItem === 'string' ? row.giftItem.trim() : ''
    // A rule naming no gift cannot be offered - an empty chip in a basket is a promise of nothing.
    if (typeof row.id !== 'number' || item === '') continue
    // `minAmount` is a string on this wire (`GiftController` answers the row untouched), so it is read as money.
    if (typeof row.minAmount !== 'string' && typeof row.minAmount !== 'number') continue
    const minAmountCents = parseMoneyToCents(row.minAmount)
    if (minAmountCents < 0) continue
    rules.push({ id: row.id, giftItem: item, minAmountCents, active: row.active !== false })
  }
  return rules
}

/** Whether a basket line is an amount gift, optionally under one rule. */
export function isAmountGiftLine(line: Pick<CartLine, 'giftRuleId' | 'giftRuleType'>, ruleId?: number): boolean {
  return line.giftRuleType === 'amount' && line.giftRuleId !== undefined && (ruleId === undefined || line.giftRuleId === ruleId)
}

/**
 * The amount gifts the basket has reached and does not hold yet.
 *
 * @param foodCents the food after discounts, without delivery - the menu knows no coupon yet and passes its
 *   subtotal; the checkout passes subtotal less the discount it computes.
 */
export function amountGiftOffers(
  rules: readonly AmountGiftRule[],
  foodCents: number,
  lines: readonly Pick<CartLine, 'giftRuleId' | 'giftRuleType'>[],
): GiftOffer[] {
  const offers: GiftOffer[] = []
  for (const rule of rules) {
    if (!rule.active || foodCents < rule.minAmountCents) continue
    if (lines.some((line) => isAmountGiftLine(line, rule.id))) continue
    offers.push({ ruleId: rule.id, ruleType: 'amount', giftName: rule.giftItem, giftPriceCents: 0, count: 1 })
  }
  return offers
}

/**
 * The amount gifts the basket holds and no longer earns - #124's *"if the order drops below, the gift is removed"*.
 *
 * **`null` rules means the read has not answered, and then nothing is taken.** A failed or pending read is not a rule
 * switched off, and emptying a basket of its gift on a network blip is the defect FB-831 measured on the other side.
 */
export function amountGiftsToRemove<Line extends Pick<CartLine, 'giftRuleId' | 'giftRuleType'>>(
  rules: readonly AmountGiftRule[] | null,
  foodCents: number,
  lines: readonly Line[],
): Line[] {
  if (rules === null) return []
  return lines.filter((line) => {
    if (!isAmountGiftLine(line)) return false
    const rule = rules.find((candidate) => candidate.id === line.giftRuleId)
    return rule === undefined || !rule.active || foodCents < rule.minAmountCents
  })
}
